/**
 * PlanComparisonTable — Tableau comparatif des avantages par tier
 * VERSION MARKETING OPTIMISÉE
 *
 * Améliorations vs v1 :
 *  - Badge "Populaire" sur Premium (plan d'ancrage)
 *  - Ligne BONUS avec features soft (favoris, partage, early access)
 *  - Gestion "∞" pour valeurs illimitées (visuellement distinct)
 *  - Preuve sociale en bas
 *  - Économie annuelle calculée
 */
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { TIERS } from '../services/PremiumService';
import { CASHBACK_RATES } from '../config/CashbackConfig';

// v1.12.268 — features définies via labelKey pour traduction runtime
const FEATURES = [
  { key: 'price',     labelKey: 'plan_feat_price',     labelFR: 'Prix',       labelEN: 'Price',       icon: '💳' },
  { key: 'wardrobe',  labelKey: 'plan_feat_wardrobe',  labelFR: 'Dressing',   labelEN: 'Wardrobe',    icon: '👗' },
  { key: 'ai',        labelKey: 'plan_feat_ai',        labelFR: 'IA/jour',    labelEN: 'AI/day',      icon: '🤖' },
  { key: 'looks',     labelKey: 'plan_feat_looks',     labelFR: 'Looks IA',   labelEN: 'AI Looks',    icon: '✨' },
  { key: 'tryon',     labelKey: 'plan_feat_tryon',     labelFR: 'Essais + Flat-lay', labelEN: 'Try-ons + Flat-lay', icon: '🪞' },
  { key: 'calendar',  labelKey: 'plan_feat_calendar',  labelFR: 'Calendrier', labelEN: 'Calendar',    icon: '📅' },
  // v1.12.911 — Trips retiré (feature Voyage reportée pour après le lancement)
  // { key: 'trips',     labelKey: 'plan_feat_trips',     labelFR: 'Voyages',    labelEN: 'Trips',       icon: '✈️' },
  { key: 'cashback',  labelKey: 'plan_feat_cashback',  labelFR: 'Crédit Mytenue',   labelEN: 'Mytenue Credits',    icon: '✨' },
  { key: 'support',   labelKey: 'plan_feat_support',   labelFR: 'Support',    labelEN: 'Support',     icon: '📧' },
];

const BONUSES = {
  free:    ['—'],
  basic:   ['Favoris illimités', 'Historique complet'],
  // ═══ FIX 2026-04 : "Alertes stock" retiré ═══
  // Cohérent avec la suppression de l'entrée homonyme dans HomeScreen v1.7.46.
  premium: ['Styliste IA+', 'Partage looks'],
  gold:    ['Early access', 'Beta tests', 'Support VIP'],
};

const getValue = (tier, feature, lang = 'fr') => {
  const t = TIERS[tier];
  if (!t) return '—';
  const en = lang === 'en';
  switch (feature) {
    case 'price':
      if (t.priceMonthly === 0) return en ? 'Free' : 'Gratuit';
      return en ? `€${t.priceMonthly.toFixed(2)}` : `${t.priceMonthly.toFixed(2).replace('.', ',')}€`;
    case 'wardrobe':
      return t.wardrobe_items === Infinity ? '∞' : `${t.wardrobe_items}`;
    case 'ai':
      return t.ai_requests === Infinity ? '∞' : `${t.ai_requests}`;
    case 'looks':
      return t.looks_per_day === Infinity ? '∞' : `${t.looks_per_day}`;
    case 'tryon':
      if (tier === 'free' || t.tryons_per_month === 0) return '—';
      if (t.tryons_per_month === Infinity) return '∞';
      return `${t.tryons_per_month}`;
    case 'calendar':
      // v1.12.268 — premium/gold = ∞ (cohérent avec Dressing gold)
      if (tier === 'free')                       return en ? '7 d'  : '7 j';
      if (tier === 'basic')                      return en ? '30 d' : '30 j';
      if (tier === 'premium' || tier === 'gold') return '∞';
      return '—';
    case 'trips':
      if (!t.trips_access) return '—';
      return t.trips_count === Infinity ? '∞' : `${t.trips_count}`;
    case 'cashback': {
      // v1.12.592 — Affichage "X €/100€" au lieu de ✓ pour clarifier le gain
      // ex: Basic = 1,80 €/100€, Premium = 3,00 €/100€, Gold = 4,80 €/100€
      const max = t.cashback?.displayMaxEurPer100;
      if (!max) return '—';
      return en ? `€${max.toFixed(2)}/€100` : `${max.toFixed(2).replace('.', ',')} €/100€`;
    }
    case 'support':
      if (tier === 'gold')    return 'VIP';
      if (tier === 'premium') return en ? 'Prio'  : 'Prio';
      if (tier === 'basic')   return en ? '48h'   : '48h';
      return en ? 'Comm.' : 'Comm.';
    default:
      return '—';
  }
};

const TIER_BADGES = {
  free:    { emoji: '',   label: 'FREE',  accent: '#888' },
  basic:   { emoji: '⭐', label: 'BASIC', accent: '#3B82F6' },
  premium: { emoji: '✨', label: 'PREM',  accent: '#8B5CF6' },
  gold:    { emoji: '👑', label: 'GOLD',  accent: '#D4AF37' },
};

export default function PlanComparisonTable({
  currentTier = 'free',
  onSelectPlan,
  showHeader = true,
  compact = false,
  showBonuses = true,
  showSocialProof = true,
}) {
  const { dark } = useTheme();
  const { lang } = useI18n();
  const en = lang === 'en';
  const s = React.useMemo(() => makeStyles(dark, compact), [dark, compact]);

  const tiers = ['free', 'basic', 'premium', 'gold'];

  const premium = TIERS.premium;
  const yearlySavings = premium.priceMonthly
    ? Math.round((1 - premium.priceYearlyMonth / premium.priceMonthly) * 100)
    : 17;

  return (
    <View style={s.container}>
      {showHeader && (
        <View style={s.headerWrap}>
          <Text style={s.headerLabel}>{en ? '💎 OUR PLANS' : '💎 NOS FORMULES'}</Text>
          <Text style={s.headerTitle}>{en ? 'Compare plans' : 'Comparer les plans'}</Text>
          <Text style={s.headerSub}>
            {en ? 'Find the subscription that fits you' : 'Trouvez l\'abonnement qui vous correspond'}
          </Text>
        </View>
      )}

      <View style={s.tableWrap}>
        {/* Ligne en-têtes tiers */}
        <View style={s.row}>
          <View style={[s.cellLeft, s.headerCellLeft]}>
            <Text style={s.cellLeftLabel}>{en ? 'Plans' : 'Plans'}</Text>
          </View>
          {tiers.map((tier, tierIdx) => {
            const badge = TIER_BADGES[tier];
            const isCurrent = tier === currentTier;
            const isPopular = TIERS[tier]?.popular;
            const isLast = tierIdx === tiers.length - 1;
            const canTap = !!onSelectPlan && !isCurrent;
            return (
              <TouchableOpacity
                key={tier}
                style={[
                  s.cellHeader,
                  isPopular && s.cellHeaderPopular,
                  isCurrent && s.cellHeaderCurrent,
                  isCurrent && { borderColor: badge.accent },
                  isLast && s.cellNoRightBorder,
                ]}
                onPress={() => canTap && onSelectPlan(tier)}
                disabled={!canTap}
                activeOpacity={canTap ? 0.7 : 1}
              >
                {isPopular && (
                  <View style={s.popularRibbon}>
                    <Text style={s.popularRibbonText}>{en ? '⭐ Popular' : '⭐ Populaire'}</Text>
                  </View>
                )}

                {!!badge.emoji && <Text style={s.tierEmoji}>{badge.emoji}</Text>}
                <Text
                  style={[s.tierLabel, { color: badge.accent }]}
                  numberOfLines={1}
                >
                  {badge.label}
                </Text>
                {isCurrent && (
                  <View style={[s.currentBadge, { backgroundColor: badge.accent }]}>
                    <Text style={s.currentBadgeText}>{en ? 'CURRENT' : 'ACTUEL'}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Lignes features */}
        {FEATURES.map((f, idx) => (
          <View key={f.key} style={[s.row, idx % 2 === 0 && s.rowStriped]}>
            <View style={s.cellLeft}>
              <Text style={s.cellLeftIcon}>{f.icon}</Text>
              <Text style={s.cellLeftLabel}>{en ? f.labelEN : f.labelFR}</Text>
            </View>
            {tiers.map((tier, tierIdx) => {
              const isCurrent = tier === currentTier;
              const isPopular = TIERS[tier]?.popular;
              const isLast = tierIdx === tiers.length - 1;
              const value = getValue(tier, f.key, lang);
              const isPrice = f.key === 'price';
              const isInfinity = value === '∞';
              return (
                <View
                  key={tier}
                  style={[
                    s.cell,
                    isPopular && s.cellPopular,
                    isCurrent && s.cellCurrent,
                    isLast && s.cellNoRightBorder,
                  ]}
                >
                  <Text
                    style={[
                      s.cellValue,
                      isPrice && s.cellValuePrice,
                      isInfinity && s.cellValueInfinity,
                      isCurrent && { color: TIER_BADGES[tier].accent, fontWeight: '700' },
                    ]}
                    numberOfLines={2}
                    adjustsFontSizeToFit
                    minimumFontScale={0.7}
                  >
                    {value}
                  </Text>
                  {isPrice && tier !== 'free' && (
                    <Text style={s.cellValueSub}>{en ? '/month' : '/mois'}</Text>
                  )}
                  {f.key === 'tryon' && value !== '—' && value !== '∞' && (
                    <Text style={s.cellValueSub}>{en ? '/month' : '/mois'}</Text>
                  )}
                </View>
              );
            })}
          </View>
        ))}

        {/* ═══ v1.10.19 : Ligne Bonus SUPPRIMÉE ═══ */}
        {/* Le bloc des bonus textuels (Favoris illimités, Styliste IA+, etc.) */}
        {/* a été retiré pour alléger visuellement le tableau et garder le focus */}
        {/* sur les compteurs chiffrés. */}
      </View>

      {/* Footer */}
      <View style={s.footerWrap}>
        {showSocialProof && (
          <Text style={s.footerSocialText}>
            {en ? '📊 Premium is chosen by most users' : '📊 Le plan Premium est choisi par la majorité'}
          </Text>
        )}
      </View>
    </View>
  );
}

const makeStyles = (dark, compact) => {
  const C = {
    bg:         dark ? '#1C1C1C' : '#FFFFFF',
    cardAlt:    dark ? '#242424' : '#FAFAFA',
    border:     dark ? '#383838' : '#E8E4DC',
    text:       dark ? '#EEEEEE' : '#1A1A1A',
    textMuted:  dark ? '#AAAAAA' : '#666',
    textFaint:  dark ? '#777777' : '#999',
    accentPale: dark ? '#332A20' : '#F5EFE8',
    popularBg:  dark ? '#2A1F3F' : '#F3EFFF',
    popularRibbonBg: '#8B5CF6',
    infinity:   '#D4AF37',
    bonusBg:    dark ? '#1F2A1F' : '#F0F9F0',
  };

  const pad = compact ? 8 : 12;
  // Les cellules utilisent flex pour remplir la largeur disponible (pas de colonne vide à droite)
  const labelFlex = compact ? 1.3 : 1.5;
  const cellFlex  = 1;

  return StyleSheet.create({
    container: {
      backgroundColor: C.bg,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: C.border,
      overflow: 'hidden',
    },
    headerWrap: {
      paddingHorizontal: pad + 2,
      paddingTop: pad + 4,
      paddingBottom: pad,
    },
    headerLabel: {
      fontSize: 10,
      letterSpacing: 2.5,
      color: C.textMuted,
      fontWeight: '700',
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: C.text,
      marginTop: 4,
      letterSpacing: 0.2,
    },
    headerSub: {
      fontSize: 11,
      color: C.textMuted,
      marginTop: 3,
    },
    tableWrap: {
      borderTopWidth: 1,
      borderTopColor: C.border,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'stretch',
    },
    rowStriped: {
      backgroundColor: C.cardAlt,
    },
    bonusRow: {
      backgroundColor: C.bonusBg,
      borderTopWidth: 1,
      borderTopColor: C.border,
    },
    cellLeft: {
      flex: labelFlex,
      paddingHorizontal: 8,
      paddingVertical: pad - 2,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderRightWidth: 1,
      borderRightColor: C.border,
    },
    headerCellLeft: {
      backgroundColor: C.cardAlt,
    },
    cellLeftIcon: {
      fontSize: 12,
    },
    cellLeftLabel: {
      fontSize: 10,
      fontWeight: '600',
      color: C.textMuted,
      letterSpacing: 0.1,
      flexShrink: 1,
    },

    cellHeader: {
      flex: cellFlex,
      paddingTop: pad + 8,
      paddingBottom: pad - 2,
      paddingHorizontal: 2,
      alignItems: 'center',
      justifyContent: 'center',
      borderRightWidth: 1,
      borderRightColor: C.border,
      backgroundColor: C.cardAlt,
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
      position: 'relative',
    },
    cellHeaderPopular: {
      backgroundColor: C.popularBg,
      borderBottomColor: C.popularRibbonBg,
    },
    cellHeaderCurrent: {
      backgroundColor: C.accentPale,
      borderBottomWidth: 2,
    },
    popularRibbon: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: C.popularRibbonBg,
      paddingVertical: 2,
      alignItems: 'center',
    },
    popularRibbonText: {
      fontSize: 8,
      fontWeight: '800',
      color: '#fff',
      letterSpacing: 0.3,
    },
    tierEmoji: {
      fontSize: 14,
      marginBottom: 2,
    },
    tierLabel: {
      fontSize: 9,
      fontWeight: '800',
      letterSpacing: 0.4,
    },
    currentBadge: {
      marginTop: 3,
      paddingHorizontal: 4,
      paddingVertical: 1,
      borderRadius: 3,
    },
    currentBadgeText: {
      fontSize: 7,
      fontWeight: '800',
      color: '#fff',
      letterSpacing: 0.3,
    },

    cell: {
      flex: cellFlex,
      paddingVertical: pad - 2,
      paddingHorizontal: 2,
      alignItems: 'center',
      justifyContent: 'center',
      borderRightWidth: 1,
      borderRightColor: C.border,
    },
    cellNoRightBorder: {
      borderRightWidth: 0,
    },
    cellBonus: {
      paddingVertical: 6,
      paddingHorizontal: 3,
      justifyContent: 'flex-start',
      minHeight: 60,
    },
    cellBonusText: {
      fontSize: 8,
      color: C.text,
      textAlign: 'center',
      lineHeight: 11,
      fontWeight: '500',
    },
    cellPopular: {
      backgroundColor: dark ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.04)',
    },
    cellCurrent: {
      backgroundColor: dark ? 'rgba(212, 184, 150, 0.08)' : 'rgba(212, 175, 55, 0.05)',
    },
    cellValue: {
      fontSize: 12,
      fontWeight: '600',
      color: C.text,
      textAlign: 'center',
    },
    cellValuePrice: {
      fontSize: 12,
      fontWeight: '700',
    },
    cellValueInfinity: {
      fontSize: 16,
      fontWeight: '700',
      color: C.infinity,
    },
    cellValueSub: {
      fontSize: 8,
      color: C.textFaint,
      marginTop: 1,
    },

    footerWrap: {
      paddingVertical: 10,
      paddingHorizontal: pad,
      backgroundColor: C.cardAlt,
      borderTopWidth: 1,
      borderTopColor: C.border,
      gap: 4,
    },
    footerSaveText: {
      fontSize: 10,
      color: C.text,
      textAlign: 'center',
      fontWeight: '600',
    },
    footerSocialText: {
      fontSize: 10,
      color: C.textMuted,
      textAlign: 'center',
    },
  });
};
